/**
 * Auth Store - Zustand-based authentication state management
 * Replaces React Context API for better performance and simpler API
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tier levels for access control
const TIER_LEVELS = { free: 0, growth: 1, enterprise: 2 };

export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      loading: true,
      error: null,

      // Computed
      isAuthenticated: () => !!get().user,
      
      hasAccess: (requiredTier) => {
        const { user } = get();
        if (!user) return requiredTier === 'free';
        const userLevel = TIER_LEVELS[user.subscription_tier] || 0;
        const requiredLevel = TIER_LEVELS[requiredTier] || 0;
        return userLevel >= requiredLevel;
      },

      // Actions
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),

      fetchUser: async () => {
        const { token } = get();
        if (!token) {
          set({ loading: false });
          return;
        }
        
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          set({ user: response.data, loading: false, error: null });
        } catch (error) {
          // Token invalid - clear auth state
          set({ token: null, user: null, loading: false });
          localStorage.removeItem('auth-storage');
        }
      },

      login: async (username, password) => {
        set({ loading: true, error: null });
        try {
          const response = await axios.post(`${API}/auth/login`, { username, password });
          const { access_token, user: userData } = response.data;
          set({ token: access_token, user: userData, loading: false });
          return userData;
        } catch (error) {
          const message = error.response?.data?.detail || 'Login failed';
          set({ error: message, loading: false });
          throw new Error(message);
        }
      },

      register: async (email, username, password, fullName = '') => {
        set({ loading: true, error: null });
        try {
          const response = await axios.post(`${API}/auth/register`, {
            email,
            username,
            password,
            full_name: fullName
          });
          const { access_token, user: userData } = response.data;
          set({ token: access_token, user: userData, loading: false });
          return userData;
        } catch (error) {
          const message = error.response?.data?.detail || 'Registration failed';
          set({ error: message, loading: false });
          throw new Error(message);
        }
      },

      logout: () => {
        set({ token: null, user: null, error: null });
        localStorage.removeItem('auth-storage');
      },

      // Direct auth setter for demo/sandbox sessions
      setAuth: (token, user) => {
        set({ token, user, loading: false, error: null });
      },

      upgradeSubscription: async (tier) => {
        const { token, fetchUser } = get();
        try {
          await axios.post(
            `${API}/auth/upgrade?tier=${tier}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          await fetchUser();
        } catch (error) {
          const message = error.response?.data?.detail || 'Upgrade failed';
          set({ error: message });
          throw new Error(message);
        }
      },

      // Initialize - call on app mount
      initialize: async () => {
        const { token, fetchUser } = get();
        if (token) {
          await fetchUser();
        } else {
          set({ loading: false });
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ token: state.token }), // Only persist token
    }
  )
);

// Legacy hook for backward compatibility with useAuth()
export const useAuth = () => {
  const store = useAuthStore();
  
  return {
    user: store.user,
    token: store.token,
    loading: store.loading,
    error: store.error,
    isAuthenticated: store.isAuthenticated(),
    login: store.login,
    register: store.register,
    logout: store.logout,
    setAuth: store.setAuth,
    upgradeSubscription: store.upgradeSubscription,
    hasAccess: store.hasAccess,
  };
};

export default useAuthStore;
